import { Chip, Tooltip } from '@mui/material'
import { CheckCircle, Info, Warning, Error as ErrorIcon, TrendingUp, Autorenew } from '@mui/icons-material'

// Every verdict the drift engine can return needs an entry here. Two were
// missing and fell through to STABLE's config, so REBASELINE rendered as a
// green "Stable" tick — telling the user nothing changed when what actually
// happened is that ARIAS changed how it measures. POSITIVE_DRIFT lost its good
// news the same way.
const VERDICT_CONFIG: Record<string, { color: any; icon: any; label: string; meaning: string }> = {
  STABLE: {
    color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} />, label: 'Stable',
    meaning: 'This agent still behaves the way it did when its certificate was issued.',
  },
  POSITIVE_DRIFT: {
    color: 'success', icon: <TrendingUp sx={{ fontSize: 16 }} />, label: 'Improved',
    meaning: 'The agent changed for the better — error handling, guardrails or bounds got stronger.',
  },
  COSMETIC_DRIFT: {
    color: 'default', icon: <Info sx={{ fontSize: 16 }} />, label: 'Cosmetic',
    meaning: 'Something changed, but nothing that alters what the agent can do. Worth a glance, not action.',
  },
  MATERIAL_DRIFT: {
    color: 'warning', icon: <Warning sx={{ fontSize: 16 }} />, label: 'Material Drift',
    meaning: 'The agent can now do something it could not before — a new tool, wider memory, a model change.',
  },
  CRITICAL_DRIFT: {
    color: 'error', icon: <ErrorIcon sx={{ fontSize: 16 }} />, label: 'Critical Drift',
    meaning: 'A high-risk capability change: a destructive tool, cross-agent memory, or a different model family.',
  },
  REBASELINE: {
    color: 'info', icon: <Autorenew sx={{ fontSize: 16 }} />, label: 'Re-baselined',
    meaning: 'ARIAS changed how it measures, not what your agent does. A fresh baseline was recorded automatically; no action needed.',
  },
}

export const VERDICT_MEANINGS = VERDICT_CONFIG

interface DriftBadgeProps {
  verdict?: string
  size?: 'small' | 'medium'
  tooltip?: string
}

const DriftBadge = ({ verdict, size = 'small', tooltip }: DriftBadgeProps) => {
  if (!verdict) return null
  // Unknown verdicts fall back to STABLE's styling but must not borrow its
  // meaning — claiming "still behaves as certified" about something we cannot
  // classify is the one wrong answer here.
  const known = VERDICT_CONFIG[verdict]
  const config = known || { ...VERDICT_CONFIG.STABLE, label: verdict, meaning: '' }
  const chip = (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="outlined"
      sx={{ fontSize: size === 'small' ? '0.7rem' : undefined }}
    />
  )
  const title = tooltip || config.meaning
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip
}

export default DriftBadge
